import { UserInteractionData } from 'core/auth/entities';
import { notAvailable } from 'core/common/constants';
import { normalizeError } from 'core/common/errors';
import {
  ATTRS_ANALYTICS_FLOW_NAME,
  ATTRS_ANALYTICS_FUNNEL_LOCALE,
  ATTRS_ANALYTICS_FUNNEL_NAME,
  SpanName,
} from 'core/common/observability/entities';
import { AppThunk } from 'core/common/store';
import { decodeRedirectDomain } from 'core/common/utils/decodeRedirectDomain';
import { sanitizeEventSourceUrl } from 'core/common/utils/sanitizeEventSourceUrl';
import { config } from 'core/config';
import { requireEmailConsentFeature } from 'core/feature-flags/config';
import { getQuizData } from 'core/funnel/store/selectors';
import { CustomerSettings, OrderData } from 'core/payments/entities';
import { updateOrderData, updateOrderId } from 'core/payments/store/actions';
import { getScan } from 'core/scan/store';
import { UserCreateOrder } from 'core/user/entities';
import { getUserInteraction, getUserLocation } from 'core/user/store/selectors';
import { getUtmTags } from '../../userUtmTags';
import { getIsUserTester, getUserInformation } from '../selectors';
import { updateUserInformation } from './updateUserInformation';

export const createOrder =
  ({ email, offerName, analyticsIds, emailFlow, businessUnit }: UserCreateOrder): AppThunk =>
  async (dispatch, getState, context) => {
    const {
      analyticsService,
      observabilitySystem,
      heatmapRecordingService,
      errorMonitoringService,
      loggingService,
      featureFlags,
      httpClient,
      authService,
      appEnvironment,
      paymentService,
      metaService,
      funnelSettingsService,
      utmTagsService,
    } = context;

    const userInformation = getUserInformation(getState());
    const isUserTester = getIsUserTester(getState());
    const quizData = getQuizData(getState());
    const userInteractionData = getUserInteraction(getState());
    const userLocation = getUserLocation(getState());
    const scan = getScan(getState());

    const mediaSourcesParams = analyticsService.getAttributionParameters();
    const funnelName = funnelSettingsService.getFunnelName();
    const flowName = funnelSettingsService.getFlowName();
    const funnelLocale = funnelSettingsService.getFunnelLocale();
    const utmTags = getUtmTags(getState());
    const sessionId = analyticsService.getSessionId();
    const deviceId = analyticsService.getDeviceId();
    const splitId = featureFlags.getSplitId();
    const isTestingEnv = appEnvironment.isTestingEnv();
    const userAgent = appEnvironment.getUserAgent();

    const isEmailConsentRequired = featureFlags.isFeatureOn(requireEmailConsentFeature);

    const emailConsent = !isEmailConsentRequired;

    const hasFacebookPixelIds = analyticsIds.facebook.length > 0;
    const hasTikTokPixelIds = analyticsIds.tikTok.length > 0;
    const hasSnapchatPixelIds = analyticsIds.snapchat.length > 0;
    const hasAnalyticsIds = hasTikTokPixelIds || hasFacebookPixelIds || hasSnapchatPixelIds;
    const meta = metaService.getMeta();
    const eventSourceUrl = sanitizeEventSourceUrl(meta.href) ?? config.mainAppUrl;

    const mediaSources = {
      ...utmTagsService.getUtmTags(),
      ...mediaSourcesParams,
      ...utmTags,
    };

    const marketingDomain = mediaSources.rf
      ? `https://${decodeRedirectDomain(mediaSources.rf)}`
      : eventSourceUrl;

    const userInteraction: UserInteractionData = [
      {
        birth_date: userInformation.date,
        isChanged: userInteractionData.date.isChanged,
      },
    ];

    const customerSettings: CustomerSettings = {
      email_consent: emailConsent,
      do_not_sell_data: false,
      report_type: userInformation.relationshipType,
      ...(hasAnalyticsIds
        ? {
            pixels: {
              ...(hasFacebookPixelIds ? { facebook: analyticsIds.facebook } : {}),
              ...(hasTikTokPixelIds ? { tik_tok: analyticsIds.tikTok } : {}),
              ...(hasSnapchatPixelIds ? { snapchat: analyticsIds.snapchat } : {}),
            },
          }
        : {}),
      emailFlow,
    };

    const orderData: OrderData = {
      subjects: [
        {
          assigned_to: 'partner_1',
          birth_place: {
            place: userInformation.location || 'Greenwich, Greater London, United Kingdom',
            longitude: userInformation.place[0] || 0.05,
            latitude: userInformation.place[1] || 51.45,
          },
          gender: userInformation.gender,
          birth_date: userInformation.date || '1990-01-01',
          birth_time: userInformation.time,
        },
        {
          assigned_to: 'partner_2',
          birth_place: {
            place: userInformation.partnerLocation || 'Greenwich, Greater London, United Kingdom',
            latitude: userInformation.partnerPlace[0] || 0.05,
            longitude: userInformation.partnerPlace[1] || 51.45,
          },
          gender: userInformation.partnerGender || 'male',
          birth_date: userInformation.partnerDate || '1990-01-01',
          birth_time: userInformation.partnerTime,
        },
      ],
      reports: [],
      scan: {
        photoId: scan.photoId,
      },
    };

    const attributes = {
      [ATTRS_ANALYTICS_FUNNEL_NAME]: funnelName,
      [ATTRS_ANALYTICS_FLOW_NAME]: flowName,
      [ATTRS_ANALYTICS_FUNNEL_LOCALE]: funnelLocale,
    };

    const createOrderSpan = observabilitySystem.startSpan(SpanName.CREATE_ORDER, {
      attributes,
    });

    const createNebulaAccountSpan = observabilitySystem.startSpan(SpanName.CREATE_NEBULA_ACCOUNT, {
      attributes,
    });

    const authenticateByNebulaIdSpan = observabilitySystem.startSpan(
      SpanName.AUTHENTICATE_BY_NEBULA_ID,
      {
        attributes,
      },
    );

    try {
      const { userId, userType } = await authService.createNebulaAccount({
        email,
        name: quizData.name,
        gender: userInformation.gender,
        birth_day: userInformation.date || '1990-01-01',
        birth_time: userInformation.time || '12:00:00',
        birth_place: userInformation.location || 'Greenwich, Greater London, United Kingdom',
        longitude: `${userInformation.place[0] || 0.05}`,
        latitude: `${userInformation.place[1] || 51.45}`,
        submitted_at: new Date().getTime(),
        raw_url: meta.href,
        funnel_interaction: {
          email,
          source: 'appnebula',
          country: userLocation.country,
          locale: funnelLocale,
          region: userLocation.region || 'NY',
          user_agent: userAgent,
          funnel_name: funnelName,
          flow_name: flowName,
          utm_params: { ...mediaSources, sessionId, deviceId },
          quiz_data: { ...quizData, raw_data: userInteraction, business_unit: businessUnit },
          settings: {
            ...customerSettings,
            split_id: splitId,
            isTester: isTestingEnv,
            domain: eventSourceUrl,
            marketing_domain: marketingDomain,
          },
        },
      });
      createOrderSpan.setUserId(userId);

      createNebulaAccountSpan.succeed();

      const { token } = await authService.authenticateByNebulaId(userId, email);

      authenticateByNebulaIdSpan.succeed();

      await httpClient.setHeaders({
        User: userId,
        'Access-Token': token,
      });

      const { orderId } = await paymentService.createOrder({
        email,
        offer_id: offerName,
        extra_data: orderData,
      });

      analyticsService.setIsUserTester(isUserTester);
      analyticsService.addEmail(email, userId);
      analyticsService.addUserId(userId);
      analyticsService.updateUserProperties({ email_consent: emailConsent });
      heatmapRecordingService.identify(email);

      dispatch(updateOrderData(orderData));
      dispatch(updateUserInformation('nebulaId', userId));
      dispatch(updateOrderId(orderId));
      dispatch(updateUserInformation('orderId', orderId));
      dispatch(updateUserInformation('userType', userType));

      createOrderSpan.succeed();
    } catch (err) {
      const error = normalizeError(err);
      loggingService.error(error);

      errorMonitoringService.captureError(error, {
        tags: {
          email,
          funnel_name: funnelName,
          emailFlow,
          get_params: location.search || notAvailable,
          error_message: error.message,
          error_reason: 'create_order',
          ...userLocation,
        },
      });

      createNebulaAccountSpan.fail(error);
      authenticateByNebulaIdSpan.fail(error);
      createOrderSpan.fail(error);
    }
  };
